import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Info,
  Linkedin,
  Mail,
  Phone,
} from 'lucide-react';
import { useState, type FC, type ReactNode } from 'react';
import { FaWhatsapp } from '@react-icons/all-files/fa/FaWhatsapp';

const PHONE_HREF = 'tel:+918427641925';
const WHATSAPP_HREF = 'https://wa.me/918427641925?text=Hello%20there!';
const EMAIL_HREF = 'mailto:info@sahilcnc.com';
const LINKEDIN_HREF = 'https://www.linkedin.com/company/sahil-cnc-machines/';

type FloatingActionsProps = {
  onOpenEnquiry: () => void;
};

type ActionItemProps = {
  label: string;
  onClick?: () => void;
  href?: string;
  icon: ReactNode;
  tone: 'quote' | 'whatsapp' | 'email' | 'call' | 'linkedin';
  withDivider?: boolean;
};

const ActionItem: FC<ActionItemProps> = ({
  label,
  onClick,
  href,
  icon,
  tone,
  withDivider,
}) => {
  const content = (
    <>
      <span className={`site-floating-actions__icon site-floating-actions__icon--${tone}`}>
        {icon}
      </span>
      <span className="site-floating-actions__label">{label}</span>
    </>
  );

  return (
    <div
      className={`site-floating-actions__item${withDivider ? ' site-floating-actions__item--divider' : ''}`}
    >
      {href ? (
        <a
          className="site-floating-actions__link"
          href={href}
          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          target={href.startsWith('http') ? '_blank' : undefined}
        >
          {content}
        </a>
      ) : (
        <button className="site-floating-actions__link" onClick={onClick} type="button">
          {content}
        </button>
      )}
    </div>
  );
};

const FloatingActions: FC<FloatingActionsProps> = ({ onOpenEnquiry }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <aside
      aria-label="Quick contact options"
      className={`site-floating-actions${isOpen ? ' site-floating-actions--open' : ' site-floating-actions--closed'}`}
    >
      <div className="site-floating-actions__drawer" id="site-floating-actions-drawer">
        <div className="site-floating-actions__panel">
          <ActionItem
            icon={<FileText aria-hidden size={22} strokeWidth={2} />}
            label="Get Quote"
            onClick={onOpenEnquiry}
            tone="quote"
            withDivider
          />
          <ActionItem
            href={WHATSAPP_HREF}
            icon={<FaWhatsapp aria-hidden size={24} />}
            label="Whatsapp"
            tone="whatsapp"
          />
          <ActionItem
            href={EMAIL_HREF}
            icon={<Mail aria-hidden size={22} strokeWidth={2} />}
            label="Email"
            tone="email"
          />
          <ActionItem
            href={PHONE_HREF}
            icon={<Phone aria-hidden size={22} strokeWidth={2} />}
            label="Call Us"
            tone="call"
          />
          <ActionItem
            href={LINKEDIN_HREF}
            icon={<Linkedin aria-hidden size={22} strokeWidth={2} />}
            label="Linkedin"
            tone="linkedin"
          />
        </div>

        <button
          className="site-floating-actions__enquiry"
          onClick={onOpenEnquiry}
          type="button"
        >
          <span className="site-floating-actions__enquiry-icon" aria-hidden>
            <Info size={28} strokeWidth={2.5} />
          </span>
          <span className="site-floating-actions__enquiry-label">Enquiry Now</span>
        </button>
      </div>

      <button
        aria-controls="site-floating-actions-drawer"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hide contact options' : 'Show contact options'}
        className="site-floating-actions__toggle"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        type="button"
      >
        {isOpen ? (
          <ChevronRight aria-hidden size={20} strokeWidth={2.5} />
        ) : (
          <ChevronLeft aria-hidden size={20} strokeWidth={2.5} />
        )}
      </button>
    </aside>
  );
};

export default FloatingActions;
